import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/shared/StatusBadge";
import type { AuditEntry } from "@/api/types";

const ACTION_LABELS: Record<string, string> = {
  "study.updated": "Study Updated",
  "site.updated": "Site Updated",
  "query.status_changed": "Query Status Changed",
  "connection.configured": "Connection Configured",
  "connection.tested": "Connection Tested",
  "sync.triggered": "Sync Triggered",
  "sync.completed": "Sync Completed",
  "sync.failed": "Sync Failed",
  "role.assigned": "Role Assigned",
  "role.revoked": "Role Revoked",
  "role.updated": "Role Updated",
  "alert.acknowledged": "Alert Acknowledged",
  "alert.resolved": "Alert Resolved",
  "alert_rule.created": "Alert Rule Created",
  "alert_rule.updated": "Alert Rule Updated",
  "alert_rule.deleted": "Alert Rule Deleted",
  "event.created": "Event Created",
  "event.updated": "Event Updated",
  "event.deleted": "Event Deleted",
  "widget.updated": "Widget Updated",
  "widget.deleted": "Widget Deleted",
  "settings.updated": "Settings Updated",
};

const ENTITY_ROUTES: Record<string, string> = {
  study: "/studies",
  site: "/sites",
  query: "/queries",
  alert: "/alerts",
  alert_rule: "/alerts",
  event: "/calendar",
  widget: "/dashboard",
  user: "/users",
  connection: "/integrations",
};

export function AuditEntryInspector({ data }: { data: unknown }) {
  const entry = data as AuditEntry;
  const entityRoute = ENTITY_ROUTES[entry.entityType];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          {ACTION_LABELS[entry.action] ?? entry.action}
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {new Date(entry.timestamp).toLocaleString()}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <StatusBadge status={entry.category} />
          <StatusBadge status={entry.entityType} />
        </div>
      </div>

      <div className="space-y-2">
        <Detail label="Action" value={ACTION_LABELS[entry.action] ?? entry.action} />
        <Detail label="Performed By" value={entry.performedByEmail ?? entry.performedBy} />
        <Detail label="Entity Type" value={entry.entityType} />
        {entry.entityLabel && <Detail label="Entity" value={entry.entityLabel} />}
        <Detail label="Entity ID" value={entry.entityId} />
        <Detail label="Category" value={entry.category} />
      </div>

      {entry.changes && entry.changes.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Changes
          </h4>
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30 text-xs">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Field</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Old</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">New</th>
                </tr>
              </thead>
              <tbody>
                {entry.changes.map((change, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 font-medium text-foreground">{change.field}</td>
                    <td className="px-3 py-2 text-red-400 line-through">{change.oldValue || "—"}</td>
                    <td className="px-3 py-2 text-green-400">{change.newValue || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {entityRoute && (
        <Link
          to={entityRoute}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          View {entry.entityType}
        </Link>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground break-all">{value}</p>
    </div>
  );
}
